import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { COACHES, coachById } from "@/data/coaches";
import CoachAvatar from "@/components/CoachAvatar";
import { Sparkles, Clock, Target, TrendingUp, ChevronRight, BarChart3, Brain, Zap, AlertTriangle, ArrowUpRight, ArrowRight, ArrowDownRight } from "lucide-react";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";
import { LogoDark } from "@/components/brand/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CoachingDashboardPage() {
  usePageTitle("Coaching Dashboard");
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [coachStats, setCoachStats] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [predictive, setPredictive] = useState(null);
  const [predictiveLoading, setPredictiveLoading] = useState(false);
  const [capscanLink, setCapscanLink] = useState(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
      return;
    }
    if (!user) return;
    let cancelled = false;
    (async () => {
      const [o, c, s, cl] = await Promise.all([
        axios.get(`${API}/api/coaching/dashboard/overview`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/api/coaching/dashboard/coaches`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/coaching/sessions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/coaching/dashboard/capscan-link`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      if (cancelled) return;
      setOverview(o.data);
      setCoachStats(c.data || []);
      setSessions(s.data || []);
      setCapscanLink(cl.data);
    })();
    return () => { cancelled = true; };
  }, [user, loading, navigate]);

  // Lazy-load analytics + predictive tabs the first time they become active.
  useEffect(() => {
    if (activeTab !== "analytics") return;
    let cancelled = false;
    (async () => {
      setAnalyticsLoading(true);
      try {
        const r = await axios.get(`${API}/api/coaching/dashboard/analytics`, { withCredentials: true });
        if (!cancelled) setAnalytics(r.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setAnalytics({ ready: false, sessions_completed: 0, sessions_needed: 3 });
      } finally {
        if (!cancelled) setAnalyticsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "predictive") return;
    let cancelled = false;
    (async () => {
      setPredictiveLoading(true);
      try {
        const r = await axios.get(`${API}/api/coaching/dashboard/predictive`, { withCredentials: true });
        if (!cancelled) setPredictive(r.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setPredictive({ ready: false, sessions_completed: 0, sessions_needed: 3 });
      } finally {
        if (!cancelled) setPredictiveLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab]);

  const refreshSessions = async () => {
    const s = await axios.get(`${API}/api/coaching/sessions`, { withCredentials: true }).catch(() => ({ data: [] }));
    setSessions(s.data || []);
  };

  const toggleAction = async (sessionId) => {
    await axios.post(`${API}/api/coaching/action/${sessionId}/toggle`, {}, { withCredentials: true });
    refreshSessions();
  };

  const [reminderPref, setReminderPref] = useState(true);
  useEffect(() => {
    if (user) setReminderPref(user.notification_preferences?.coaching_reminders !== false);
  }, [user]);

  const toggleReminderPref = async () => {
    const next = !reminderPref;
    try {
      await axios.post(`${API}/api/coaching/preferences/reminders`, { enabled: next }, { withCredentials: true });
      setReminderPref(next);
    } catch (e) { console.error(e); }
  };

  if (loading || !user) return <div className="min-h-screen bg-cream flex items-center justify-center pt-16"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  const tabs = [
    { id: "overview", name: "Overview" },
    { id: "coaches", name: "My Coaches" },
    { id: "history", name: "Session History" },
    { id: "analytics", name: "Analytics" },
    { id: "predictive", name: "Predictive Insights" },
    { id: "capscan", name: "My CapScan Link" },
  ];

  const statById = (cid) => coachStats.find((c) => c.coach_id === cid);

  return (
    <div className="min-h-screen bg-cream pt-24 pb-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Branded dashboard header — Version A on navy */}
        <div className="bg-navy rounded-2xl px-6 py-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="coaching-dashboard-header">
          <LogoDark width={180} height={42} />
          <span className="font-dm text-gold/80 text-[11px] uppercase tracking-[0.2em]">Coaching Suite</span>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="font-playfair text-navy text-3xl font-bold">My Coaching</h1>
            <p className="font-dm text-navy/60 text-sm">Your sessions, insights, and growth.</p>
          </div>
          <Link to="/coaching/start" data-testid="dashboard-start-cta" className="bg-gold text-navy font-dm font-bold px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-gold/90">
            <Sparkles className="w-4 h-4" /> New Session
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-6">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`coaching-tab-${t.id}`}
              className={`font-dm text-sm px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === t.id ? "bg-navy text-white" : "bg-white text-navy/60 hover:text-navy"}`}>
              {t.name}
            </button>
          ))}
        </div>

        {/* Overview */}
        {activeTab === "overview" && overview && (
          <div data-testid="overview-tab">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {[
                { lab: "Sessions", val: overview.total_sessions, icon: Sparkles },
                { lab: "Hours coached", val: overview.total_hours, icon: Clock },
                { lab: "Actions", val: `${overview.actions_completed}/${overview.actions.length}`, sub: `${overview.actions_completion_pct}% done`, icon: Target },
                { lab: "Avg confidence", val: overview.avg_confidence || "—", sub: "out of 10", icon: TrendingUp },
              ].map((s) => (
                <div key={s.lab} className="bg-white rounded-xl p-4 shadow-sm border-t-4 border-gold">
                  <s.icon className="w-5 h-5 text-gold mb-2" />
                  <p className="font-playfair text-navy text-2xl font-bold">{s.val}</p>
                  <p className="font-dm text-navy/50 text-xs">{s.lab}</p>
                  {s.sub && <p className="font-dm text-navy/40 text-[10px]">{s.sub}</p>}
                </div>
              ))}
            </div>

            {/* Quick start with top coaches */}
            {(overview.top_coach_ids || []).length > 0 && (
              <div className="bg-white rounded-xl p-5 mb-6">
                <p className="font-dm text-navy/60 text-xs uppercase font-bold mb-3">Continue with your coaches</p>
                <div className="flex flex-wrap gap-3">
                  {overview.top_coach_ids.map((cid) => {
                    const c = coachById(cid);
                    if (!c) return null;
                    return (
                      <Link key={cid} to="/coaching/start" className="flex items-center gap-2 bg-cream rounded-full pl-1 pr-4 py-1 hover:bg-gold/10 transition-colors">
                        <CoachAvatar coach={c} size={32} />
                        <span className="font-dm text-navy text-xs">{c.name.split(" ")[0]}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Last session */}
            {overview.last_session && overview.last_session.status === "completed" && (
              <div className="bg-white rounded-xl p-5 mb-6" data-testid="last-session-card">
                <p className="font-dm text-navy/40 text-xs uppercase font-bold mb-2">Last session</p>
                <p className="font-playfair text-navy text-lg font-bold mb-1">{overview.last_session.coach_name} · {overview.last_session.topic}</p>
                <p className="font-dm text-navy/60 text-xs mb-3">{new Date(overview.last_session.ended_at || overview.last_session.started_at).toLocaleDateString("en-IN")} · {overview.last_session.length_minutes} min</p>
                {overview.last_session.key_insight && <div className="bg-cream rounded-lg p-3 border-l-4 border-gold mb-2"><p className="font-dm text-navy/80 text-sm italic">{overview.last_session.key_insight}</p></div>}
                {overview.last_session.action_committed && <p className="font-dm text-navy/70 text-sm"><span className="text-navy/40">Action:</span> {overview.last_session.action_committed}</p>}
              </div>
            )}

            {/* Open actions */}
            {overview.actions.length > 0 && (
              <div className="bg-white rounded-xl p-5" data-testid="open-actions">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-dm text-navy/40 text-xs uppercase font-bold">Your commitments</p>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="reminder-pref-toggle">
                    <span className="font-dm text-navy/60 text-[11px]">3-day reminder emails</span>
                    <input type="checkbox" checked={reminderPref} onChange={toggleReminderPref} className="accent-gold w-3.5 h-3.5" />
                  </label>
                </div>
                <div className="space-y-2">
                  {overview.actions.map((a) => (
                    <div key={a.session_id} className="flex items-start gap-3 p-3 bg-cream rounded-lg">
                      <input type="checkbox" checked={a.done} onChange={() => toggleAction(a.session_id)} data-testid={`action-checkbox-${a.session_id}`} className="mt-1 accent-gold w-4 h-4" />
                      <div className="flex-1">
                        <p className={`font-dm text-sm ${a.done ? "line-through text-navy/40" : "text-navy"}`}>{a.action}</p>
                        <p className="font-dm text-navy/40 text-xs">{a.coach_name} · {a.topic}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {overview.total_sessions === 0 && (
              <div className="bg-white rounded-xl p-10 text-center">
                <p className="font-dm text-navy/60 text-sm mb-4">You haven’t completed any coaching sessions yet.</p>
                <Link to="/coaching/start" className="inline-block bg-gold text-navy font-dm font-bold px-6 py-2.5 rounded-lg">Start your first session</Link>
              </div>
            )}
          </div>
        )}

        {/* My Coaches */}
        {activeTab === "coaches" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coaches-tab">
            {COACHES.map((c) => {
              const stat = statById(c.id);
              const isTop = coachStats[0]?.coach_id === c.id && stat?.sessions > 0;
              return (
                <div key={c.id} className={`bg-white rounded-xl p-5 ${isTop ? "ring-2 ring-gold" : ""}`} data-testid={`coach-tile-${c.id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <CoachAvatar coach={c} size={56} ring />
                    <div className="flex-1">
                      <p className="font-playfair text-navy font-bold leading-tight">{c.name}</p>
                      <p className="font-dm text-navy/50 text-[10px]">{c.flag} {c.origin} · {c.level}</p>
                      {isTop && <span className="text-[9px] bg-gold/20 text-gold font-bold px-2 py-0.5 rounded-full">Most used</span>}
                    </div>
                  </div>
                  <p className="font-dm text-navy/60 text-xs mb-3">{c.style}</p>
                  <div className="flex items-center justify-between border-t border-navy/5 pt-3">
                    <div>
                      <p className="font-dm text-navy/40 text-[10px] uppercase">Sessions</p>
                      <p className="font-playfair text-navy font-bold">{stat?.sessions || 0}</p>
                    </div>
                    <div>
                      <p className="font-dm text-navy/40 text-[10px] uppercase">Action follow-through</p>
                      <p className="font-playfair text-navy font-bold">{stat ? `${stat.compatibility}%` : "—"}</p>
                    </div>
                    <Link to="/coaching/start" className="bg-navy text-gold font-dm text-xs font-bold px-3 py-1.5 rounded-lg">{stat?.sessions ? "Session" : "Try"}</Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Session History */}
        {activeTab === "history" && (
          <div className="bg-white rounded-xl overflow-hidden" data-testid="history-tab">
            {sessions.length === 0 ? (
              <p className="font-dm text-navy/60 text-sm text-center py-10">No sessions yet.</p>
            ) : (
              <table className="w-full">
                <thead className="bg-navy/5"><tr>
                  <th className="text-left font-dm text-navy/60 text-xs uppercase py-3 px-4">Date</th>
                  <th className="text-left font-dm text-navy/60 text-xs uppercase py-3 px-4">Coach</th>
                  <th className="text-left font-dm text-navy/60 text-xs uppercase py-3 px-4">Topic</th>
                  <th className="text-left font-dm text-navy/60 text-xs uppercase py-3 px-4 hidden sm:table-cell">Length</th>
                  <th className="text-left font-dm text-navy/60 text-xs uppercase py-3 px-4 hidden md:table-cell">Insight</th>
                  <th className="text-left font-dm text-navy/60 text-xs uppercase py-3 px-4">Status</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {sessions.map((s) => {
                    const c = coachById(s.coach_id);
                    return (
                      <tr key={s._id} className="border-b border-navy/5 hover:bg-cream cursor-pointer" onClick={() => navigate(`/coaching/session/${s._id}`)} data-testid={`history-row-${s._id}`}>
                        <td className="font-dm text-navy/70 text-xs py-3 px-4">{new Date(s.started_at).toLocaleDateString("en-IN")}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <CoachAvatar coach={c} size={28} />
                            <span className="font-dm text-navy text-xs">{s.coach_name}</span>
                          </div>
                        </td>
                        <td className="font-dm text-navy text-xs py-3 px-4">{s.topic}</td>
                        <td className="font-dm text-navy/70 text-xs py-3 px-4 hidden sm:table-cell">{s.length_minutes} min</td>
                        <td className="font-dm text-navy/70 text-xs py-3 px-4 hidden md:table-cell max-w-xs truncate italic">{s.key_insight || "—"}</td>
                        <td className="py-3 px-4">
                          <span className={`font-dm text-[10px] font-bold px-2 py-0.5 rounded-full ${s.status === "completed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{s.status}</span>
                        </td>
                        <td className="py-3 px-2"><ChevronRight className="w-4 h-4 text-navy/30" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Analytics */}
        {activeTab === "analytics" && (
          <div data-testid="analytics-tab">
            {analyticsLoading && <div className="bg-white rounded-xl p-10 text-center"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="font-dm text-navy/60 text-sm">Analysing your coaching language patterns…</p></div>}
            {!analyticsLoading && analytics && !analytics.ready && (
              <div className="bg-white rounded-xl p-10 text-center" data-testid="analytics-not-ready">
                <BarChart3 className="w-10 h-10 text-gold/50 mx-auto mb-3" />
                <h3 className="font-playfair text-navy text-xl font-bold mb-1">Analytics unlocks after {analytics.sessions_needed} more session{analytics.sessions_needed !== 1 ? "s" : ""}</h3>
                <p className="font-dm text-navy/60 text-sm mb-5">Language pattern analysis and recurring themes need at least 3 completed sessions to surface meaningful signal. You have {analytics.sessions_completed}.</p>
                <Link to="/coaching/start" className="inline-block bg-gold text-navy font-dm font-bold px-6 py-2.5 rounded-lg">Start your next session</Link>
              </div>
            )}
            {!analyticsLoading && analytics?.ready && (
              <div className="space-y-5">
                {analytics.degraded && (
                  <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4" data-testid="analytics-degraded">
                    <p className="font-dm text-amber-800 text-sm"><strong>Insights temporarily unavailable</strong> — we’re showing what we have. Detailed pattern analysis will refresh automatically.</p>
                  </div>
                )}
                {/* Language patterns */}
                <div className="bg-white rounded-xl p-5">
                  <h3 className="font-playfair text-navy text-lg font-bold mb-1">Language Pattern Analysis</h3>
                  <p className="font-dm text-navy/50 text-xs mb-4">How you speak about your situations across all sessions.</p>
                  <div className="space-y-4">
                    {[
                      { lab: "Ownership ('I')", v: analytics.language_patterns?.ownership_language || 0, c: "#639922" },
                      { lab: "Victim ('they/it')", v: analytics.language_patterns?.victim_language || 0, c: "#E24B4A" },
                      { lab: "Possibility", v: analytics.language_patterns?.possibility_language || 0, c: "#C9A84C" },
                      { lab: "Scarcity", v: analytics.language_patterns?.scarcity_language || 0, c: "#EF9F27" },
                    ].map((p) => (
                      <div key={p.lab}>
                        <div className="flex justify-between mb-1"><span className="font-dm text-navy text-xs font-semibold">{p.lab}</span><span className="font-dm text-navy font-bold text-xs">{p.v}%</span></div>
                        <div className="h-3 bg-navy/5 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${p.v}%`, backgroundColor: p.c }} /></div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recurring themes */}
                <div className="bg-white rounded-xl p-5">
                  <h3 className="font-playfair text-navy text-lg font-bold mb-1">Recurring Themes</h3>
                  <p className="font-dm text-navy/50 text-xs mb-4">Topics that keep appearing across your sessions.</p>
                  {(analytics.recurring_themes || []).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {analytics.recurring_themes.map((t, i) => {
                        const cls = t.frequency_type === "high_frequency" ? "bg-red-100 text-red-700" : t.frequency_type === "growing" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700";
                        return <span key={i} className={`font-dm text-xs font-bold px-3 py-1.5 rounded-full ${cls}`} data-testid={`theme-chip-${i}`}>{t.theme}</span>;
                      })}
                    </div>
                  ) : (
                    <p className="font-dm text-navy/50 text-sm italic">Themes will surface once we have richer signal across more sessions.</p>
                  )}
                </div>

                {/* Confidence trajectory */}
                <div className="bg-white rounded-xl p-5">
                  <h3 className="font-playfair text-navy text-lg font-bold mb-1">Confidence Trajectory</h3>
                  <p className="font-dm text-navy/50 text-xs mb-4">Your average confidence rating per month (out of 10).</p>
                  {(analytics.confidence_trajectory || []).length >= 2 ? (
                    <div className="flex items-end gap-3 h-40">
                      {analytics.confidence_trajectory.map((c, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-2">
                          <div className="w-full bg-navy rounded-t flex items-end justify-center text-gold font-bold text-xs pt-1" style={{ height: `${(c.avg / 10) * 100}%`, minHeight: "20px" }}>{c.avg}</div>
                          <span className="font-dm text-navy/50 text-[10px]">{c.month}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="font-dm text-navy/50 text-sm italic">Trajectory unlocks once you’ve coached across 2+ months.</p>
                  )}
                </div>

                {/* Coach compatibility */}
                <div className="bg-white rounded-xl p-5">
                  <h3 className="font-playfair text-navy text-lg font-bold mb-1">Coach Compatibility</h3>
                  <p className="font-dm text-navy/50 text-xs mb-4">Action follow-through rate by coach (higher = your actions get done).</p>
                  {(analytics.coach_compatibility || []).length > 0 ? (
                    <div className="space-y-2">
                      {analytics.coach_compatibility.map((c) => {
                        const co = coachById(c.coach_id);
                        return (
                          <div key={c.coach_id} className="flex items-center gap-3">
                            <CoachAvatar coach={co} size={32} className="flex-shrink-0" />
                            <span className="font-dm text-navy text-xs w-28 truncate">{co?.name}</span>
                            <div className="flex-1 h-3 bg-navy/5 rounded-full overflow-hidden"><div className="h-full bg-gold" style={{ width: `${c.completion_rate}%` }} /></div>
                            <span className="font-dm text-navy/70 font-bold text-xs w-12 text-right">{c.completion_rate}%</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="font-dm text-navy/50 text-sm italic">Mark your action commitments complete to unlock follow-through scores.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Predictive Insights */}
        {activeTab === "predictive" && (
          <div data-testid="predictive-tab">
            {predictiveLoading && <div className="bg-white rounded-xl p-10 text-center"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="font-dm text-navy/60 text-sm">Reading the signals in your conversations…</p></div>}
            {!predictiveLoading && predictive && !predictive.ready && (
              <div className="bg-white rounded-xl p-10 text-center" data-testid="predictive-not-ready">
                <Brain className="w-10 h-10 text-gold/50 mx-auto mb-3" />
                <h3 className="font-playfair text-navy text-xl font-bold mb-1">Predictive insights unlock after {predictive.sessions_needed} more session{predictive.sessions_needed !== 1 ? "s" : ""}</h3>
                <p className="font-dm text-navy/60 text-sm mb-5">Emerging capabilities and risks need ≥3 completed sessions of conversation to surface. You have {predictive.sessions_completed}.</p>
                <Link to="/coaching/start" className="inline-block bg-gold text-navy font-dm font-bold px-6 py-2.5 rounded-lg">Start your next session</Link>
              </div>
            )}
            {!predictiveLoading && predictive?.ready && (
              <div className="space-y-5">
                {predictive.degraded && (
                  <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4" data-testid="predictive-degraded">
                    <p className="font-dm text-amber-800 text-sm"><strong>Insights temporarily unavailable</strong> — we’ll regenerate these signals on your next visit.</p>
                  </div>
                )}
                <div className="bg-navy rounded-xl p-6 text-white">
                  <p className="font-dm text-gold text-xs uppercase tracking-wide mb-1">Predictive Skill Intelligence</p>
                  <h3 className="font-playfair text-3xl font-bold">Based on your coaching conversations</h3>
                </div>

                {/* Emerging capabilities */}
                <div>
                  <h3 className="font-playfair text-navy text-lg font-bold mb-3 flex items-center gap-2"><Zap className="w-5 h-5 text-gold" /> Emerging Capabilities</h3>
                  {(predictive.emerging_capabilities || []).length > 0 ? (
                    <div className="grid sm:grid-cols-3 gap-3">
                      {predictive.emerging_capabilities.map((e, i) => (
                        <div key={i} className="bg-gold/10 border-l-4 border-gold rounded-r-xl p-4" data-testid={`emerging-${i}`}>
                          <div className="flex justify-between items-start mb-2"><p className="font-playfair text-navy font-bold text-sm leading-tight">{e.skill_name}</p><span className="bg-gold text-navy font-dm text-[10px] font-bold px-2 py-0.5 rounded-full">{e.confidence_percentage}%</span></div>
                          <p className="font-dm text-navy/70 text-xs mb-2">{e.reasoning}</p>
                          <p className="font-dm text-navy text-xs italic">→ {e.coaching_recommendation}</p>
                          <Link to="/coaching/start" className="mt-3 inline-block bg-navy text-gold font-dm text-[10px] font-bold px-3 py-1.5 rounded-lg">Coach me on this</Link>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl p-5 text-center"><p className="font-dm text-navy/50 text-sm italic">Capability signals will emerge after a few more sessions.</p></div>
                  )}
                </div>

                {/* Capability risks */}
                <div>
                  <h3 className="font-playfair text-navy text-lg font-bold mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" /> Capability Risks</h3>
                  {(predictive.capability_risks || []).length > 0 ? (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {predictive.capability_risks.map((r, i) => (
                        <div key={i} className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-4" data-testid={`risk-${i}`}>
                          <div className="flex justify-between items-start mb-2"><p className="font-playfair text-navy font-bold text-sm leading-tight">{r.risk_name}</p><span className="bg-red-500 text-white font-dm text-[10px] font-bold px-2 py-0.5 rounded-full">{r.confidence_percentage}%</span></div>
                          <p className="font-dm text-navy/70 text-xs mb-2">{r.reasoning}</p>
                          <p className="font-dm text-navy text-xs italic">→ {r.coaching_recommendation}</p>
                          <Link to="/coaching/start" className="mt-3 inline-block bg-navy text-gold font-dm text-[10px] font-bold px-3 py-1.5 rounded-lg">Address this</Link>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl p-5 text-center"><p className="font-dm text-navy/50 text-sm italic">No risk patterns detected yet. Continue coaching to surface signals.</p></div>
                  )}
                </div>

                {/* Behaviour momentum */}
                <div className="bg-white rounded-xl p-5">
                  <h3 className="font-playfair text-navy text-lg font-bold mb-3">Behaviour Momentum</h3>
                  {(predictive.behaviour_momentum || []).length > 0 ? (
                    <div className="space-y-2">
                      {predictive.behaviour_momentum.map((b, i) => {
                        const isUp = b.state === "Growing";
                        const isFlat = b.state === "Flat";
                        const Icon = isUp ? ArrowUpRight : isFlat ? ArrowRight : ArrowDownRight;
                        const colour = isUp ? "text-green-600 bg-green-50" : isFlat ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50";
                        const dotColour = isUp ? "bg-green-500" : isFlat ? "bg-amber-500" : "bg-red-500";
                        return (
                          <div key={i} className={`flex items-center justify-between rounded-lg p-3 ${colour}`} data-testid={`momentum-${i}`}>
                            <div className="flex items-center gap-3"><span className={`w-2.5 h-2.5 rounded-full ${dotColour}`} /><span className="font-dm text-navy text-sm">{b.behaviour}</span></div>
                            <div className="flex items-center gap-1"><Icon className="w-4 h-4" /><span className="font-dm text-xs font-bold">{b.state}</span></div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="font-dm text-navy/50 text-sm italic">Momentum signals will surface as your sessions accumulate.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* My CapScan Link */}
        {activeTab === "capscan" && (
          <div data-testid="capscan-link-tab">
            {!capscanLink || !capscanLink.has_capscan ? (
              <div className="bg-white rounded-xl p-10 text-center">
                <p className="font-dm text-navy/60 text-sm mb-4">Complete your 4D CapScan to get personalised coaching recommendations.</p>
                <Link to="/capscan/new" className="inline-block bg-gold text-navy font-dm font-bold px-6 py-2.5 rounded-lg">Start CapScan</Link>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="bg-white rounded-xl p-5">
                  <p className="font-dm text-navy/40 text-xs uppercase font-bold mb-2">4D Capability Portrait — {capscanLink.employee_name}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {capscanLink.dimension_scores.map((d) => {
                      const pct = d.target ? Math.min(100, (d.score / d.target) * 100) : 0;
                      return (
                        <div key={d.dimension} className="bg-cream rounded-lg p-3">
                          <p className="font-dm text-navy/50 text-[10px] uppercase font-bold">{d.dimension.replace(" Power", "")}</p>
                          <p className="font-playfair text-navy text-2xl font-bold">{d.score}<span className="text-xs text-navy/50">/{d.target}</span></p>
                          <div className="h-2 bg-navy/10 rounded-full mt-1 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {(capscanLink.addressed_gaps || []).length > 0 && (
                  <div className="bg-green-50 border-l-4 border-green-500 rounded-r-xl p-5">
                    <h3 className="font-playfair text-navy text-lg font-bold mb-2">✓ Gaps you’re already coaching on</h3>
                    <ul className="space-y-1">
                      {capscanLink.addressed_gaps.map((g, i) => (
                        <li key={i} className="font-dm text-navy text-sm">{g.skill} <span className="text-navy/50 text-xs">— Zone {g.now} → {g.required}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {(capscanLink.unaddressed_gaps || []).length > 0 && (
                  <div className="bg-white rounded-xl p-5">
                    <h3 className="font-playfair text-navy text-lg font-bold mb-3">Untouched critical gaps</h3>
                    <div className="space-y-2">
                      {capscanLink.unaddressed_gaps.map((g, i) => (
                        <div key={i} className="flex items-center justify-between bg-cream rounded-lg p-3" data-testid={`unaddressed-${i}`}>
                          <div><p className="font-dm text-navy text-sm font-bold">{g.skill}</p><p className="font-dm text-navy/50 text-xs">Zone {g.now} → Zone {g.required}</p></div>
                          <Link to={`/coaching/start?topic=${encodeURIComponent(g.skill)}&capscan_id=${capscanLink.report_id}&gap=${encodeURIComponent(g.skill)}`} className="bg-navy text-gold font-dm text-xs font-bold px-3 py-1.5 rounded-lg">Coach me</Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Link to={`/capscan/report/${capscanLink.report_id}`} className="inline-block text-navy/60 hover:text-gold font-dm text-sm">View full CapScan report →</Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
